import React, { useEffect } from "react"
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { Bar } from "react-chartjs-2"
import { useDispatch } from "react-redux"
import { getDashboardKpiRatings } from "store/actions/dashboardAction"
import { useSelector } from "react-redux"
import { ShimmerThumbnail } from "react-shimmer-effects"

const BranchRatings = ({
  ratingModal,
  setRatingModal,
  ratingBranches,
  setRatingBranches,
  branchOptions,
  branchListOptions,
  setIsAllSelected,
  functionRef,
  commonDayType,
  commonDateRange,
  timesUTC,
  convertTimeToUTCDate,
  t,
  language,
  hasRatingsPermission,
}) => {
  const dispatch = useDispatch()
  const { loading, dashboardRatingsData } = useSelector(
    state => state.Dashboard
  )

  useEffect(() => {
    getDashboardRatings()
  }, [commonDateRange, ratingBranches])

  const getDashboardRatings = () => {
    // Only fetch if user has Ratings permission
    if (!hasRatingsPermission) return
    
    const updatedBranches = ratingBranches?.map(item => item.value) || []
    const isMoreThanOneMonth = isDateRangeGreaterThanOneMonth(
      commonDateRange.start,
      commonDateRange.end
    )
    const updatedType =
      commonDayType === "day"
        ? "hour"
        : commonDayType === "week" || commonDayType === "month"
        ? "date"
        : commonDayType === "custom" && isMoreThanOneMonth
        ? "month"
        : "date"
    const payload = {
      branches: updatedBranches,
      dateRange: commonDateRange,
      type: updatedType,
    }
    dispatch(getDashboardKpiRatings(payload))
  }

  useEffect(() => {
    if (functionRef) {
      functionRef.current = getDashboardRatings
    }
  }, [functionRef])

  function isDateRangeGreaterThanOneMonth(startDate, endDate) {
    const start = new Date(startDate)
    const end = new Date(endDate)

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error("Invalid date format")
      return false
    }

    // Create a new date one month after the start date
    const oneMonthLater = new Date(start)
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)

    // Compare with the end date
    return end > oneMonthLater
  }

  const colors = ["#556ee6", "#f1b44c", "#f46a6a", "#50a5f1", "#34c38f"]

  const getChartOptions = item => ({
    labels: item?.countPerScale?.map(scale =>
      item.countPerScale.length > 5
        ? `${scale.scaleValue} ${t("Scale")}`
        : `${scale.scaleValue} ${t("Star")}`
    ),
    legend: { show: !1 },
    colors: colors,
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            total: {
              show: true,
              color: "#000",
              showAlways: true,
              label: `${item.totalRatingCount} ${t("Reviews")}`,
              formatter: () => item.averageRating,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: val => `${Math.round(val)}%`,
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.45,
      },
      style: {
        fontSize: "10px",
        colors: ["#000"],
      },
      // background: {
      //   enabled: true,
      //   foreColor: "#fff", // Text color inside the background
      //   padding: 4,
      //   borderRadius: 2,
      //   borderWidth: 1,
      //   borderColor: "#ccc",
      // },
    },
    theme: {
      monochrome: {
        enabled: true,
        // color: "#3800ff",
      },
    },
    // responsive: [
    //   {
    //     breakpoint: 480,
    //     options: {
    //       chart: {
    //         width: 200,
    //       },
    //         legend: {
    //           position: "bottom",
    //         },
    //     },
    //   },
    // ],
  })

  const getChartSeries = item => item.countPerScale.map(scale => scale.count)

  const calculatePercentage = (count, total) =>
    ((count / total) * 100).toFixed(1)

  const getBarChartOptions = (item, chartType) => {
    if (!item || !item.monthlyData || !Array.isArray(item.monthlyData)) {
      console.error("Invalid item data:", item)
      return {
        chart: { type: "bar" },
        xaxis: { categories: ["No Data"] },
        // ... other default options
      }
    }

    let categories
    try {
      categories =
        chartType === "week"
          ? item.monthlyData.map(data => data.date || "Unknown")
          : chartType === "day"
          ? timesUTC.map(time => {
              // Parse the time string
              const [hour, period] = time.split(" ")
              let hours = parseInt(hour)

              // Convert to 24-hour format
              if (period === "PM" && hours !== 12) hours += 12
              if (period === "AM" && hours === 12) hours = 0

              // Create a Date object for today's date at the specified UTC time
              const date = new Date()
              date.setUTCHours(hours, 0, 0, 0)

              // Convert to local time string
              return date.toLocaleString(undefined, {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })
            })
          : chartType === "custom" &&
            isDateRangeGreaterThanOneMonth(
              commonDateRange.start,
              commonDateRange.end
            )
          ? item.monthlyData.map(data => {
              const year = data.year || new Date().getFullYear()
              return new Date(year, data.month - 1).toLocaleString("default", {
                month: "short",
              })
            })
          : item.monthlyData.map(data => data.date || "Unknown")
    } catch (error) {
      console.error("Error processing categories:", error)
      categories = ["Error"]
    }

    return {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "25%",
          endingShape: "rounded",
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: categories,
        labels: { show: true },
      },
      colors: colors,
      legend: { position: "bottom" },
      fill: { opacity: 1 },
    }
  }

  const getBarChartSeries = (item, chartType) => {
    const data = chartType === "day" ? item.monthlyData : item.monthlyData

    const allScaleValues = new Set()
    data.forEach(hourData => {
      hourData.countPerScale.forEach(scale => {
        allScaleValues.add(scale.scaleValue)
      })
    })

    const sortedScaleValues = Array.from(allScaleValues).sort((a, b) => a - b)
    const hasMoreThanFiveScales = data.some(
      hourData => hourData.countPerScale.length > 5
    )

    const series = sortedScaleValues.map(scaleValue => ({
      name: hasMoreThanFiveScales
        ? `${scaleValue} ${t("Scale")}`
        : `${scaleValue} ${t("Star")}`,
      data: data.map(hourData => {
        const scaleData = hourData.countPerScale.find(
          scale => scale.scaleValue === scaleValue
        )
        return scaleData ? scaleData.count : 0
      }),
    }))

    return series
  }

  const getAggregatedBarChartData = () => {
    const allBranchData = dashboardRatingsData.branchData.flatMap(item =>
      item.branches.map(branch => ({
        questionId: item._id,
        questionEn: language === "sa" ? item?.question?.ar : item?.question?.en,
        ...branch,
      }))
    )

    // Group branches by name
    const groupedBranches = allBranchData.reduce((acc, branch) => {
      const key = language === "sa" ? branch.branchNameAr : branch.branchName
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(branch)
      return acc
    }, {})
    const branchNames = Object.keys(groupedBranches)
    const datasets = dashboardRatingsData.branchData.map((item, index) => ({
      label: language === "sa" ? item?.question?.ar : item?.question?.en,
      data: branchNames.map(branchName => {
        const branch = groupedBranches[branchName].find(
          b => b.questionId === item._id
        )
        return branch ? branch.averageRating : 0
      }),
      backgroundColor: `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${
        Math.random() * 255
      }, 0.6)`,
    }))

    return {
      labels: branchNames,
      datasets: datasets,
    }
  }

  const barChartOptions = {
    scales: {
      x: {
        title: {
          display: true,
          text: t("Branches Name"),
        },
      },
      y: {
        beginAtZero: true,
        max: 10,
        title: {
          display: true,
          text: t("Average Ratings"),
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          title: tooltipItems => {
            return tooltipItems[0].label // Branch name
          },
          label: context => {
            const datasetLabel = context.dataset.label || ""
            return `${datasetLabel}: ${context.parsed.y.toFixed(2)}`
          },
        },
      },
    },
  }

  return (
    <div>
      {loading ? (
        <Row>
          {[0, 1, 2, 3].map(item => (
            <Col lg={6} key={item}>
              <Card>
                <CardBody>
                  <ShimmerThumbnail height={344} rounded />
                </CardBody>
              </Card>
            </Col>
          ))}
          <Col lg={12}>
            <Card>
              <CardBody>
                <ShimmerThumbnail height={400} rounded />
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            {dashboardRatingsData?.donutData?.map((item, index) => (
              <>
                <Col lg={6} key={index}>
                  <Card className="card_100_24">
                    <CardBody>
                      <CardTitle>
                        {language === "sa"
                          ? item?.question?.ar
                          : item?.question?.en}
                      </CardTitle>
                      <Row className="d-flex align-items-center h-90">
                        <Col lg={7}>
                          <div style={{ direction: "ltr" }}>
                            <div id="donut-chart">
                              <ReactApexChart
                                options={getChartOptions(item)}
                                series={getChartSeries(item)}
                                type="donut"
                                height={250}
                                className="apex-charts"
                              />
                            </div>
                          </div>
                        </Col>
                        <Col lg={5} className="pt-3">
                          {item.countPerScale.map((scale, scaleIndex) => (
                            <Row
                              className="d-flex align-items-center mt-2"
                              key={scaleIndex}
                            >
                              <Col
                                style={{
                                  maxWidth: 30,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span>
                                  {scale.scaleValue}{" "}
                                  {item.countPerScale.length > 5 ? (
                                    <i className="bx bx-ruler"></i>
                                  ) : (
                                    <i className="bx bxs-star"></i>
                                  )}
                                </span>
                              </Col>
                              <Col>
                                <div className="progress animated-progess progress-sm">
                                  <div
                                    className="progress-bar bg-primary"
                                    role="progressbar"
                                    style={{
                                      width: `${calculatePercentage(
                                        scale.count,
                                        item.totalRatingCount
                                      )}%`,
                                    }}
                                    aria-valuenow={calculatePercentage(
                                      scale.count,
                                      item.totalRatingCount
                                    )}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  ></div>
                                </div>
                              </Col>
                              <Col
                                style={{
                                  maxWidth: 80,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <div>
                                  <p>
                                    {scale.count}{" "}
                                    <small>
                                      (
                                      {calculatePercentage(
                                        scale.count,
                                        item.totalRatingCount
                                      )}
                                      %)
                                    </small>
                                  </p>
                                </div>
                              </Col>
                            </Row>
                          ))}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
                {/* APEX CHART */}
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle>
                        {language === "sa"
                          ? item?.question?.ar
                          : item?.question?.en}
                      </CardTitle>
                      <div style={{ direction: "ltr" }}>
                        <ReactApexChart
                          options={getBarChartOptions(
                            dashboardRatingsData.stackedData[index],
                            commonDayType
                          )}
                          series={getBarChartSeries(
                            dashboardRatingsData.stackedData[index],
                            commonDayType
                          )}
                          type="bar"
                          height="320"
                          className="apex-charts"
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </>
            ))}
          </Row>
          <Row>
            {dashboardRatingsData &&
              dashboardRatingsData.branchData &&
              dashboardRatingsData.branchData.length > 0 && (
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle>{t("Branches Average Ratings")}</CardTitle>
                      <Bar
                        data={getAggregatedBarChartData()}
                        options={barChartOptions}
                        height={120}
                      />
                    </CardBody>
                  </Card>
                </Col>
              )}
          </Row>
        </>
      )}
      {/* SHOW NOT FOUND MSG */}
      {dashboardRatingsData?.donutData?.length === 0 && (
        <Row>
          <Col>
            <Card>
              <CardBody>
                <p className="text-center py-3">{t("No ratings found.")}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default BranchRatings
